import { useState, useRef, useEffect, useMemo } from "react";
import { Search, X, ChevronDown } from "lucide-react";
import type { Tool } from "@/types/tool";

interface ToolNameSearchProps {
  tools: Tool[];
  onSelect: (toolId: string) => void;
}

const ToolNameSearch = ({ tools, onSelect }: ToolNameSearchProps) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sortedTools = useMemo(
    () => [...tools].sort((a, b) => a.name.localeCompare(b.name)),
    [tools]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTools;
    return sortedTools.filter((t) => t.name.toLowerCase().includes(q));
  }, [sortedTools, query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (toolId: string) => {
    onSelect(toolId);
    setOpen(false);
    setQuery("");
    const el = document.getElementById(`tool-card-${toolId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-foreground/30", "rounded-2xl");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-foreground/30", "rounded-2xl");
      }, 1800);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-full border bg-secondary/60 transition-all duration-200 ${
          open ? "border-foreground/20 bg-card shadow-sm" : "border-border"
        }`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Find a tool by name..."
          className="flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground outline-none min-w-0"
        />
        {query ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setQuery("");
              inputRef.current?.focus();
            }}
            className="p-0.5 rounded-md hover:bg-secondary text-muted-foreground"
            aria-label="Clear search"
          >
            <X className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown
            className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1.5 max-h-72 overflow-y-auto rounded-xl border border-border bg-card shadow-lg z-50 animate-fade-in">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground text-center">
              No tools found
            </p>
          ) : (
            <ul className="py-1">
              {filtered.map((tool) => (
                <li key={tool.id}>
                  <button
                    onClick={() => handleSelect(tool.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-secondary/80 transition-colors min-h-[44px]"
                  >
                    <img
                      src={tool.logo}
                      alt=""
                      loading="lazy"
                      className="w-5 h-5 rounded object-contain flex-shrink-0"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.visibility = "hidden";
                      }}
                    />
                    <span className="text-xs text-foreground font-medium truncate flex-1">
                      {tool.name}
                    </span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {tool.category}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ToolNameSearch;
